import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';

function formatTempo(minutos) {
  if (minutos == null) return '-';
  if (minutos < 60) return `${minutos}min`;
  if (minutos < 1440) {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  const dias = Math.floor(minutos / 1440);
  const horasResto = Math.floor((minutos % 1440) / 60);
  return horasResto > 0 ? `${dias}d ${horasResto}h` : `${dias}d`;
}

const grupoConfig = {
  rapido: { label: 'Rapido', sublabel: '≤ 24 horas', color: '#2e7d32', bg: '#e8f5e9' },
  medio: { label: 'Medio', sublabel: '2-3 dias', color: '#1976d2', bg: '#e3f2fd' },
  demorado: { label: 'Demorado', sublabel: '4-7 dias', color: '#f57c00', bg: '#fff3e0' },
  muitoDemorado: { label: 'Muito Demorado', sublabel: '> 7 dias', color: '#c62828', bg: '#ffebee' }
};

function TempoExames() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtroGrupo, setFiltroGrupo] = useState('todos');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('descricao');
  const [sortOrder, setSortOrder] = useState('ASC');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (inicio, fim) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (inicio) params.append('dataInicio', inicio);
      if (fim) params.append('dataFim', fim);
      const response = await axios.get(`${API_URL}/dashboard/tat?${params.toString()}`);
      setData(response.data);
      if (!inicio && response.data.periodo) {
        setDataInicio(response.data.periodo.dataInicio);
        setDataFim(response.data.periodo.dataFim);
      }
    } catch (err) {
      console.error('Erro ao buscar TAT:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    fetchData(dataInicio, dataFim);
  };

  const handleSort = (campo) => {
    if (sortBy === campo) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(campo);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (campo) => {
    if (sortBy !== campo) return ' ↕';
    return sortOrder === 'ASC' ? ' ↑' : ' ↓';
  };

  const examesFiltrados = () => {
    if (!data) return [];
    let lista = [...data.exames];
    if (filtroGrupo !== 'todos') {
      lista = lista.filter(e => e.grupo === filtroGrupo);
    }
    if (search) {
      const s = search.toLowerCase();
      lista = lista.filter(e =>
        e.descricao.toLowerCase().includes(s) || e.codigo.toLowerCase().includes(s)
      );
    }
    lista.sort((a, b) => {
      let va, vb;
      if (sortBy === 'descricao') { va = a.descricao; vb = b.descricao; }
      else if (sortBy === 'mediaMinutos') { va = a.mediaMinutos; vb = b.mediaMinutos; }
      else if (sortBy === 'medianaMinutos') { va = a.medianaMinutos; vb = b.medianaMinutos; }
      else if (sortBy === 'totalAmostras') { va = a.totalAmostras; vb = b.totalAmostras; }
      else if (sortBy === 'minMinutos') { va = a.minMinutos; vb = b.minMinutos; }
      else if (sortBy === 'maxMinutos') { va = a.maxMinutos; vb = b.maxMinutos; }
      else { va = a[sortBy]; vb = b[sortBy]; }
      if (typeof va === 'string') {
        return sortOrder === 'ASC' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortOrder === 'ASC' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return lista;
  };

  const lista = examesFiltrados();

  return (
    <div className="data-page" style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', fontFamily: 'Inter, Roboto, "Open Sans", sans-serif' }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>⏱️</span>
          <div>
            <h1 style={styles.pageTitle}>Tempo Medio de Exames</h1>
            <p style={styles.pageSubtitle}>
              {data ? `${data.resumo.totalExames} tipos de exames analisados` : 'Carregando...'}
              {loading && <span style={styles.loadingIndicator}> Carregando...</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Filtro periodo */}
      <div style={styles.filterRow}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Inicio</label>
          <input
            type="date"
            value={dataInicio}
            onChange={e => setDataInicio(e.target.value)}
            style={styles.filterInput}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Fim</label>
          <input
            type="date"
            value={dataFim}
            onChange={e => setDataFim(e.target.value)}
            style={styles.filterInput}
          />
        </div>
        <button onClick={handleBuscar} style={styles.btnBuscar}>Buscar</button>
      </div>

      {/* Cards resumo */}
      {data && (
        <div style={styles.cardsRow}>
          {['rapido', 'medio', 'demorado', 'muitoDemorado'].map(grupo => {
            const cfg = grupoConfig[grupo];
            const info = data.resumo[grupo];
            return (
              <div
                key={grupo}
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${cfg.color}`,
                  cursor: 'pointer',
                  backgroundColor: filtroGrupo === grupo ? cfg.bg : '#fff'
                }}
                onClick={() => setFiltroGrupo(filtroGrupo === grupo ? 'todos' : grupo)}
              >
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>{cfg.label}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: cfg.color }}>{info.quantidade}</div>
                <div style={{ fontSize: '0.75rem', color: '#999' }}>{info.percentual}% - {cfg.sublabel}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros tabela */}
      <div style={styles.tableControls}>
        <input
          type="text"
          placeholder="Buscar por nome ou codigo..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={filtroGrupo}
          onChange={e => setFiltroGrupo(e.target.value)}
          style={styles.selectFilter}
        >
          <option value="todos">Todos os grupos</option>
          <option value="rapido">Rapido (≤ 24h)</option>
          <option value="medio">Medio (2-3 dias)</option>
          <option value="demorado">Demorado (4-7 dias)</option>
          <option value="muitoDemorado">Muito Demorado (&gt; 7 dias)</option>
        </select>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>
          {lista.length} {lista.length === 1 ? 'exame' : 'exames'}
        </span>
      </div>

      {/* Tabela desktop */}
      <div className="desktop-only" style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th} onClick={() => handleSort('codigo')}>Codigo{getSortIcon('codigo')}</th>
              <th style={{ ...styles.th, textAlign: 'left' }} onClick={() => handleSort('descricao')}>Descricao{getSortIcon('descricao')}</th>
              <th style={styles.th} onClick={() => handleSort('mediaMinutos')}>Tempo Medio{getSortIcon('mediaMinutos')}</th>
              <th style={styles.th} onClick={() => handleSort('medianaMinutos')}>Mediana{getSortIcon('medianaMinutos')}</th>
              <th style={styles.th} onClick={() => handleSort('minMinutos')}>Min{getSortIcon('minMinutos')}</th>
              <th style={styles.th} onClick={() => handleSort('maxMinutos')}>Max{getSortIcon('maxMinutos')}</th>
              <th style={styles.th} onClick={() => handleSort('totalAmostras')}>Amostras{getSortIcon('totalAmostras')}</th>
              <th style={styles.th}>Prazo Nominal</th>
              <th style={styles.th}>Grupo</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</td></tr>
            )}
            {!loading && lista.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhum exame encontrado</td></tr>
            )}
            {!loading && lista.map((exame, idx) => {
              const cfg = grupoConfig[exame.grupo];
              const nominalMin = (exame.diasEntregaNominal || 0) * 1440;
              const ratio = nominalMin > 0 ? Math.min(exame.mediaMinutos / nominalMin, 2) : 0;
              const barColor = ratio <= 1 ? '#4caf50' : ratio <= 1.5 ? '#ff9800' : '#f44336';
              return (
                <tr key={exame.codigo} onClick={() => navigate(`/tempo-exames/${exame.codigo}`)} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }} title="Ver evolucao temporal">
                  <td style={styles.td}>{exame.codigo}</td>
                  <td style={{ ...styles.td, textAlign: 'left', fontWeight: '500' }}>{exame.descricao}</td>
                  <td style={styles.td}>
                    <div>{formatTempo(exame.mediaMinutos)}</div>
                    {nominalMin > 0 && (
                      <div style={{ marginTop: '4px', height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px', width: '80px', margin: '4px auto 0' }}>
                        <div style={{ height: '4px', backgroundColor: barColor, borderRadius: '2px', width: `${Math.min(ratio * 100, 100)}%` }} />
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>{formatTempo(exame.medianaMinutos)}</td>
                  <td style={styles.td}>{formatTempo(exame.minMinutos)}</td>
                  <td style={styles.td}>{formatTempo(exame.maxMinutos)}</td>
                  <td style={styles.td}>{exame.totalAmostras.toLocaleString('pt-BR')}</td>
                  <td style={styles.td}>{exame.diasEntregaNominal != null ? `${exame.diasEntregaNominal}d` : '-'}</td>
                  <td style={styles.td}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: cfg.color,
                      backgroundColor: cfg.bg
                    }}>
                      {cfg.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="mobile-only">
        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</div>}
        {!loading && lista.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhum exame encontrado</div>}
        {!loading && lista.map(exame => {
          const cfg = grupoConfig[exame.grupo];
          return (
            <div key={exame.codigo} onClick={() => navigate(`/tempo-exames/${exame.codigo}`)} style={{ ...styles.mobileCard, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#333' }}>{exame.descricao}</div>
                  <div style={{ fontSize: '0.8rem', color: '#999' }}>{exame.codigo}</div>
                </div>
                <span style={{
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: cfg.color,
                  backgroundColor: cfg.bg,
                  whiteSpace: 'nowrap'
                }}>
                  {cfg.label}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div><span style={styles.mobileLabel}>Tempo Medio:</span> <strong>{formatTempo(exame.mediaMinutos)}</strong></div>
                <div><span style={styles.mobileLabel}>Mediana:</span> <strong>{formatTempo(exame.medianaMinutos)}</strong></div>
                <div><span style={styles.mobileLabel}>Min:</span> {formatTempo(exame.minMinutos)}</div>
                <div><span style={styles.mobileLabel}>Max:</span> {formatTempo(exame.maxMinutos)}</div>
                <div><span style={styles.mobileLabel}>Amostras:</span> {exame.totalAmostras.toLocaleString('pt-BR')}</div>
                <div><span style={styles.mobileLabel}>Prazo:</span> {exame.diasEntregaNominal != null ? `${exame.diasEntregaNominal}d` : '-'}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  loadingIndicator: { marginLeft: '8px', color: '#1976d2' },
  filterRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '0.8rem', color: '#666', fontWeight: '500' },
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    backgroundColor: '#fff'
  },
  btnBuscar: {
    padding: '8px 20px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'background-color 0.2s'
  },
  tableControls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  searchInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    flex: '1',
    minWidth: '200px',
    backgroundColor: '#fff'
  },
  selectFilter: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    backgroundColor: '#fff'
  },
  tableWrapper: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'auto'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#666',
    borderBottom: '2px solid #eee',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none'
  },
  td: {
    padding: '10px 16px',
    textAlign: 'center',
    fontSize: '0.85rem',
    color: '#333',
    borderBottom: '1px solid #f0f0f0'
  },
  mobileCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  mobileLabel: { fontSize: '0.75rem', color: '#999' }
};

export default TempoExames;
